import React, { useEffect, useState } from "react";
import { dateTime } from "@/lib/format";
import { useNavigate, useParams } from "react-router-dom";
import { Phone } from "lucide-react";
import { useApp } from "@/state/AppContext";
import { api, apiErrorMessage } from "@/lib/api";
import { StatusTimeline } from "@/components/StatusTimeline";
import { StarRating } from "@/components/StarRating";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const LIVE_STATUSES = ["requested", "accepted", "dispatched", "arriving", "arrived", "in_service"];
const CANCELLABLE = ["requested", "accepted", "dispatched", "arriving", "arrived"];
const POLL_MS = 8000;

/** Ambulance status/tracking page — also the target of the `/ambulance/{id}` in-app notification
 * link that already fires today (see notify() calls in ambulance.py); this route makes it work. */
export default function AmbulanceStatus() {
  const { id } = useParams();
  const { t, lang, tenant } = useApp();
  const navigate = useNavigate();
  const [booking, setBooking] = useState(null);
  const [config, setConfig] = useState(null);
  const [missing, setMissing] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [stars, setStars] = useState(0);
  const [review, setReview] = useState("");

  const load = () =>
    api
      .get(`/ambulance/bookings/${id}`)
      .then((r) => setBooking(r.data))
      .catch(() => setMissing(true));

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // The emergency numbers (108 / 112) and the nudge timeout, for the "call now" fallback while a
  // request is still unaccepted.
  useEffect(() => {
    if (!tenant) return;
    api.get("/ambulance/config", { params: { tenant_id: tenant.id } }).then((r) => setConfig(r.data)).catch(() => undefined);
  }, [tenant]);

  useEffect(() => {
    if (!booking || !LIVE_STATUSES.includes(booking.status)) return;
    const interval = setInterval(load, POLL_MS);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [booking?.status]);

  const cancel = async () => {
    setBusy(true);
    setError("");
    try {
      await api.post(`/ambulance/bookings/${id}/cancel`, { reason });
      await load();
      setConfirmCancel(false);
    } catch (e) {
      setError(apiErrorMessage(e, t("errors.generic")));
    } finally {
      setBusy(false);
    }
  };

  const submitRating = async () => {
    setBusy(true);
    setError("");
    try {
      await api.post(`/ambulance/bookings/${id}/rate`, { rating: stars, review });
      await load();
    } catch (e) {
      setError(apiErrorMessage(e, t("errors.generic")));
    } finally {
      setBusy(false);
    }
  };

  if (missing)
    return (
      <div className="mx-auto max-w-2xl px-5 py-24 text-center" data-testid="ambulance-status-not-found">
        <p className="text-lg">{t("errors.generic")}</p>
        <Button className="mt-6 rounded-full" onClick={() => navigate("/ambulance")}>
          {t("common.back")}
        </Button>
      </div>
    );
  if (!booking) return <div className="px-5 py-24 text-center text-muted-foreground">{t("common.loading")}</div>;

  const live = LIVE_STATUSES.includes(booking.status);

  return (
    <div className="mx-auto max-w-2xl px-5 py-12 sm:px-8" data-testid="ambulance-status-page">
      <div className={`rounded-2xl p-6 text-white sm:p-8 ${live ? "bg-destructive" : "bg-[hsl(var(--primary))]"}`}>
        <p className="text-sm text-white/80">{t("ambulance.statusTitle")}</p>
        <h1 className="mt-1 font-heading text-3xl font-bold tracking-tight" data-testid="ambulance-status-headline">
          {t(`ambulance.statusLabels.${booking.status}`)}
        </h1>
        {booking.status === "requested" && (
          <div className="mt-3 space-y-3">
            {booking.unaccepted_notified_at ? (
              // (c) timed out unaccepted: the urgent "call 108/112 now" banner.
              <p className="text-sm font-extrabold" data-testid="ambulance-unaccepted-banner">
                {t("ambulance.unacceptedBanner")}
              </p>
            ) : (
              // (b) still waiting; call 108/112 if no one accepts within N minutes.
              <p className="text-sm text-white/90" data-testid="ambulance-waiting-hint">
                {t("ambulance.waitingHint", { minutes: config?.unaccepted_timeout_minutes ?? 5 })}
              </p>
            )}
            <div className="flex gap-3">
              <a
                href={`tel:${config?.emergency_numbers?.[0]?.number ?? "108"}`}
                data-testid="ambulance-status-call-108-btn"
                className="flex min-h-[44px] flex-1 items-center justify-center gap-2 rounded-full bg-white px-4 font-bold text-destructive"
              >
                <Phone size={15} /> {t("ambulance.call108")}
              </a>
              <a
                href={`tel:${config?.emergency_numbers?.[1]?.number ?? "112"}`}
                data-testid="ambulance-status-call-112-btn"
                className="flex min-h-[44px] flex-1 items-center justify-center gap-2 rounded-full bg-white px-4 font-bold text-destructive"
              >
                <Phone size={15} /> {t("ambulance.call112")}
              </a>
            </div>
          </div>
        )}
        {booking.eta_minutes !== null && live && booking.status !== "requested" && (
          <p className="mt-2 font-semibold" data-testid="ambulance-eta">
            {t("ambulance.eta", { minutes: booking.eta_minutes })}
          </p>
        )}
      </div>

      <div className="mt-6 space-y-6">
        <div className="rounded-2xl border border-border p-5">
          <StatusTimeline timeline={booking.timeline} labelsKey="ambulance.statusLabels" testId="ambulance-timeline" />
        </div>

        {booking.provider_id && (
          <div className="rounded-2xl border border-border p-5" data-testid="ambulance-provider-card">
            <p className="font-heading text-lg font-semibold">{booking.provider_name}</p>
            <div className="mt-2 flex flex-wrap gap-6 text-sm">
              {booking.vehicle_number && (
                <div>
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">{t("ambulance.vehicle")}</p>
                  <p className="font-mono font-semibold" data-testid="ambulance-vehicle">
                    {booking.vehicle_number}
                  </p>
                </div>
              )}
              {booking.driver_name && (
                <div>
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">{t("ambulance.driver")}</p>
                  <p className="font-semibold">{booking.driver_name}</p>
                </div>
              )}
            </div>
            <a
              href={`tel:${booking.driver_phone || booking.provider_phone}`}
              className="mt-4 inline-flex min-h-[40px] items-center gap-2 rounded-full bg-[hsl(var(--primary))] px-4 text-sm font-semibold text-primary-foreground"
              data-testid="ambulance-call-provider-btn"
            >
              <Phone size={14} /> {t("ambulance.callProvider")}
            </a>
          </div>
        )}

        <div className="rounded-2xl border border-border p-5 text-sm">
          <Row label={t("ambulance.pickupLabel")} value={booking.pickup_address} />
          {booking.pickup_landmark && <Row label={t("ambulance.landmark")} value={booking.pickup_landmark} />}
          {booking.destination && <Row label={t("ambulance.destinationLabel")} value={booking.destination} />}
          <Row label={t("ambulance.requestedAt")} value={dateTime(booking.created_at, lang)} />
        </div>

        {error && (
          <p className="text-sm text-destructive" data-testid="ambulance-error">
            {error}
          </p>
        )}

        {CANCELLABLE.includes(booking.status) &&
          (confirmCancel ? (
            <div className="space-y-3 rounded-2xl border border-border p-5" data-testid="ambulance-cancel-confirm">
              <p>{t("ambulance.cancelConfirm")}</p>
              <Input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={t("ambulance.cancelReason")}
                data-testid="ambulance-cancel-reason-input"
              />
              <div className="flex gap-2">
                <Button className="rounded-full" disabled={busy} onClick={cancel} data-testid="ambulance-cancel-confirm-btn">
                  {t("ambulance.cancel")}
                </Button>
                <Button variant="ghost" onClick={() => setConfirmCancel(false)}>
                  {t("ambulance.keepRequest")}
                </Button>
              </div>
            </div>
          ) : (
            <Button variant="outline" className="rounded-full" onClick={() => setConfirmCancel(true)} data-testid="ambulance-cancel-btn">
              {t("ambulance.cancel")}
            </Button>
          ))}

        {booking.status === "completed" &&
          (booking.rating ? (
            <p className="rounded-2xl border border-border p-5 text-sm" data-testid="ambulance-rated">
              {t("ambulance.rated")}
            </p>
          ) : (
            <div className="space-y-3 rounded-2xl border border-border p-5" data-testid="ambulance-rate-card">
              <p className="font-semibold">{t("ambulance.rateTitle")}</p>
              <p className="text-sm text-muted-foreground">{t("ambulance.rateHint")}</p>
              <StarRating value={stars} onChange={setStars} size={26} testId="ambulance-rate-stars" />
              <Input value={review} onChange={(e) => setReview(e.target.value)} data-testid="ambulance-review-input" />
              <Button className="rounded-full" disabled={stars === 0 || busy} onClick={submitRating} data-testid="ambulance-rate-submit-btn">
                {t("ambulance.rateSubmit")}
              </Button>
            </div>
          ))}

        {(booking.timeline ?? []).length > 0 && (
          <div className="space-y-2 rounded-2xl border border-border p-5" data-testid="ambulance-audit">
            <p className="font-semibold">{t("ambulance.auditTitle")}</p>
            {booking.timeline.map((entry, index) => (
              <div key={index} className="flex items-center gap-2 text-sm">
                <Badge variant="outline">{t(`ambulance.statusLabels.${entry.status}`)}</Badge>
                <span className="text-muted-foreground">
                  {dateTime(entry.at, lang)}
                  {entry.note ? ` · ${entry.note}` : ""}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-border py-2 last:border-0">
      <span className="text-xs uppercase tracking-wide text-muted-foreground">{label}</span>
      <span className="text-right">{value}</span>
    </div>
  );
}
